import { useEffect, useRef, useState } from 'react'

/**
 * Typewriter-style reveal of streamed text — a standalone port of
 * assistant-ui's `useSmooth` (its markdown element only exists inside the
 * assistant-ui runtime, so we carry the algorithm ourselves).
 *
 * The animator drains the gap between what is displayed and the latest
 * target: each frame reveals as many characters as the elapsed time allows,
 * with the per-character interval shrinking as the backlog grows (never
 * slower than `maxCharIntervalMs`, and the whole backlog within `drainMs`).
 * Net effect: bursty 100 ms network flushes render as a steady stream, and
 * the reveal catches up quickly once the source settles.
 *
 * Auto-disabled under `prefers-reduced-motion: reduce`.
 */
export interface SmoothOptions {
  /** Time budget to drain the current backlog (default 250 ms). */
  drainMs?: number
  /** Slowest reveal pace, ms per character (default 5). */
  maxCharIntervalMs?: number
  /** Upper bound on characters revealed in one frame (default unlimited). */
  maxCharsPerFrame?: number
}

const DEFAULT_DRAIN_MS = 250
const DEFAULT_MAX_CHAR_INTERVAL_MS = 5

class TextStreamAnimator {
  frame: number | null = null
  lastUpdate = 0
  target = ''
  current = ''
  drainMs = DEFAULT_DRAIN_MS
  maxCharIntervalMs = DEFAULT_MAX_CHAR_INTERVAL_MS
  maxCharsPerFrame = Infinity

  constructor(private readonly commit: (text: string) => void) {}

  start() {
    if (this.frame !== null) return
    this.lastUpdate = performance.now()
    this.frame = requestAnimationFrame(this.tick)
  }

  stop() {
    if (this.frame !== null) cancelAnimationFrame(this.frame)
    this.frame = null
  }

  private tick = () => {
    this.frame = null
    const now = performance.now()
    let budget = now - this.lastUpdate
    const remaining = this.target.length - this.current.length
    if (remaining <= 0) return
    const perChar = Math.min(this.maxCharIntervalMs, this.drainMs / remaining)
    const limit = Math.min(remaining, this.maxCharsPerFrame)
    let chars = 0
    while (budget >= perChar && chars < limit) {
      chars++
      budget -= perChar
    }
    if (chars === limit && limit === this.maxCharsPerFrame) budget = 0
    if (chars > 0) {
      this.current = this.target.slice(0, this.current.length + chars)
      this.lastUpdate = now - budget
      this.commit(this.current)
    }
    if (this.current.length < this.target.length) this.frame = requestAnimationFrame(this.tick)
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * @param text      the latest full text (grows while streaming)
 * @param streaming whether more text may still arrive
 * @returns the text to display and whether the reveal is still catching up
 */
export function useSmoothText(
  text: string,
  streaming: boolean,
  options?: SmoothOptions
): { text: string; animating: boolean } {
  const enabled = !prefersReducedMotion()
  const [displayed, setDisplayed] = useState(() => (streaming && enabled ? '' : text))
  const animator = useRef<TextStreamAnimator | null>(null)
  if (!animator.current) animator.current = new TextStreamAnimator(setDisplayed)
  const a = animator.current

  a.drainMs = options?.drainMs ?? DEFAULT_DRAIN_MS
  a.maxCharIntervalMs = options?.maxCharIntervalMs ?? DEFAULT_MAX_CHAR_INTERVAL_MS
  a.maxCharsPerFrame = options?.maxCharsPerFrame ?? Infinity

  useEffect(() => {
    if (!enabled) {
      a.stop()
      a.current = text
      a.target = text
      setDisplayed(text)
      return
    }
    // The source was replaced (not appended to): restart from the new text.
    if (!text.startsWith(a.current)) {
      a.current = streaming ? '' : text
      setDisplayed(a.current)
    }
    a.target = text
    if (!streaming && a.current === '') {
      // Settled before anything was revealed — never leave an empty bubble.
      a.stop()
      a.current = text
      setDisplayed(text)
      return
    }
    a.start()
  }, [a, enabled, text, streaming])

  useEffect(() => () => a.stop(), [a])

  const shown = enabled ? displayed : text
  return { text: shown, animating: enabled && shown !== text }
}
